/* TerminalRenderer.js */

"use strict";
/* global ace*/
const _ = require("lodash");
const HIGHLIGHT_RULES = {
    start: [{ regex: /(^\[31m)/, token: "startOfError", next: "error" }, { defaultToken: "default" }],
    error: [{ regex: /\[m/, token: "endOfError", next: "start" }, { defaultToken: "error" }]
};

class TerminalRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("terminal-gl");
        this._rootElem.component = this._model;

        this._initialRender = true;
        this._initializeAndConfigureEditor();

        setTimeout(() => {
            this._terminal.getSession().setValue(this._model.buffer);
            this._registerListeners();
        });

        this._resizeDebouncer = _.debounce(() => {
            let maxHeight = this._getTerminalHeight() + "px";
            this._terminal.container.style["height"] = maxHeight;
            this._terminal.container.style["max-height"] = maxHeight;
            this._terminal.resize();
        }, 500);
    }

    _initializeAndConfigureEditor() {
        let editorDiv = document.createElement("div");
        editorDiv.classList.add("terminal");
        this._rootElem.append(editorDiv);

        let basePath = "/resources/ace-builds/src-min-noconflict";
        if (window.location.pathname !== "/" && window.location.pathname !== "/index.html") {
            basePath = window.location.pathname + basePath;
        }
        ace.config.set("basePath", basePath);
        this._terminal = ace.edit(editorDiv, {
            showGutter: false,
            showInvisibles: false,
            behavioursEnabled: false,
            showPrintMargin: false
        });
        this._applyHighlightRulesOnEditor(HIGHLIGHT_RULES);
        this._showSearch = this._showSearch.bind(this);
        this._terminal.commands.addCommand({
            name: "unfind",
            bindKey: {
                win: "Ctrl-F",
                mac: "Command-F"
            },
            exec: this._showSearch
        });
        this._terminal.setReadOnly(true);
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown() {
        let prom = new Promise((resolve /*,reject*/) => {
            this._unregisterListeners();
            this._model = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;
            this._terminal.destroy();
            this._terminal = undefined;
            this._initialRender = undefined;
            resolve();
        });
        return prom;
    }
    _registerListeners() {
        this._model.on("Shutdown", (this._onShutdown = this._onShutdown.bind(this)));
        this._model.on("DataWritten", (this._onDataWritten = this._onDataWritten.bind(this)));
        this._model.on("Cleared", (this._onCleared = this._onCleared.bind(this)));
        this._model.on("Resize", (this._onResize = this._onResize.bind(this)));
    }
    _unregisterListeners() {
        this._model.removeListener("Shutdown", this._onShutdown);
        this._model.removeListener("DataWritten", this._onDataWritten);
        this._model.removeListener("Cleared", this._onCleared);
        this._model.removeListener("Resize", this._onResize);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        if (this._initialRender) {
            this._initialRender = false;
            return new Promise((resolve /*,reject*/) => {
                setTimeout(() => {
                    this._resizeTerminal();
                    resolve();
                }, 0);
            });
        } else {
            this._resizeTerminal();
            return Promise.resolve();
        }
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/
    _onShutdown() {
        this._shutdown();
    }
    _onDataWritten(data /*,entireBuffer*/) {
        this._writeDataToTerminal(data);
    }
    _onCleared() {
        this._terminal.setValue("");
    }
    _onResize() {
        this._resizeTerminal();
    }
    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/
    _applyHighlightRulesOnEditor(rules) {
        let editorSession = this._terminal.getSession();
        let mode = editorSession.$mode;
        let Tokenizer = ace.require("ace/tokenizer").Tokenizer;
        let tokenizer = new Tokenizer(rules);
        mode.$tokenizer = tokenizer;
        editorSession.bgTokenizer.setTokenizer(mode.getTokenizer());
    }

    _resizeTerminal() {
        this._resizeDebouncer.cancel();
        this._resizeDebouncer();
    }

    _getTerminalHeight() {
        let rect = this._rootElem.parentElement.getBoundingClientRect();
        return rect.height;
    }

    _writeDataToTerminal(data) {
        let editorSession = this._terminal.getSession();
        editorSession.insert(
            {
                row: editorSession.getLength(),
                column: 0
            },
            data
        );
        this._terminal.scrollToRow(editorSession.getLength() - 1);
    }

    _showSearch() {
        if (this._terminal.searchBox) {
            this._terminal.searchBox.show();
        } else {
            ace.config.loadModule("ace/ext/searchbox", (e) => {
                e.Search(this._terminal);
            });
        }
    }
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}
module.exports = TerminalRenderer;
